import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ExternalLink,
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertCircle,
  Copy,
  Calendar,
  Shield,
  Key,
  Clock,
  Eye,
  EyeOff,
  Check,
  Settings,
  ChevronDown,
  Video,
  Zap,
  Lock,
} from 'lucide-react';
import {
  AdminGetGoogleAuthUrl,
  AdminHandleGoogleOAuth2Callback,
  AdminCheckGoogleOAuth2Status
} from '../components/Api.jsx';

export default function GoogleOAuth2Page() {
  const [oauthStatus, setOauthStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  const [callbackCode, setCallbackCode] = useState('');
  const [step, setStep] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [copied, setCopied] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const toastTimeoutRef = useRef(null);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, type === 'success' ? 5000 : 4000);
  };

  useEffect(() => {
    return () => { if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current); };
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.get('code') && !urlParams.get('error')) checkOAuth2Status();
  }, []);

  const checkOAuth2Status = async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const response = await AdminCheckGoogleOAuth2Status();
      setOauthStatus(response.data);
      if (response.data.status === 'configured') setStep(4);
      else if (response.data.status === 'expired') setStep(3);
      else setStep(1);
    } catch (error) {
      setOauthStatus({ status: 'error', message: 'Error: ' + error.message });
      setStep(1);
    } finally {
      isRefresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const getGoogleAuthUrl = async () => {
    setLoading(true);
    try {
      const response = await AdminGetGoogleAuthUrl();
      if (response.data.status === 'success') {
        setAuthUrl(response.data.authUrl);
        setStep(2);
        window.open(response.data.authUrl, '_blank');
      } else {
        showToast('Error getting authorization URL: ' + response.data.message, 'error');
      }
    } catch (error) {
      showToast('Error: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth2Callback = async () => {
    if (!callbackCode.trim()) { showToast('Please enter the authorization code', 'error'); return; }
    setLoading(true);
    try {
      const response = await AdminHandleGoogleOAuth2Callback(callbackCode);
      if (response.data.status === 'success') {
        showToast('Google OAuth2 connected successfully!', 'success');
        setCallbackCode('');
        setStep(4);
        await checkOAuth2Status();
      } else {
        showToast('OAuth2 failed: ' + response.data.message, 'error');
        setStep(1);
      }
    } catch (error) {
      showToast('Error: ' + error.message, 'error');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    if (code) {
      setCallbackCode(code);
      setStep(3);
      handleOAuth2CallbackWithCode(code);
    } else if (error) {
      showToast('Authorization failed: ' + error, 'error');
      setStep(1);
    }
  }, []);

  const handleOAuth2CallbackWithCode = async (code) => {
    setLoading(true);
    try {
      const response = await AdminHandleGoogleOAuth2Callback(code);
      if (response.data.status === 'success') {
        showToast('Google OAuth2 connected successfully!', 'success');
        setStep(4);
        await checkOAuth2Status();
      } else {
        showToast('OAuth2 failed: ' + response.data.message, 'error');
        setStep(1);
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      showToast('Error: ' + error.message, 'error');
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const isConnected = oauthStatus?.status === 'configured';
  const isExpired = oauthStatus?.status === 'expired';

  const configuredDate = oauthStatus?.tokenCreatedAt
    ? new Date(oauthStatus.tokenCreatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  const steps = [
    { number: 1, label: 'Get URL' },
    { number: 2, label: 'Authorize' },
    { number: 3, label: 'Enter Code' },
    { number: 4, label: 'Done' },
  ];

  const howSteps = [
    { icon: Zap, title: 'Generate Auth URL', desc: 'The system creates a secure Google OAuth2 link scoped to your calendar & Meet permissions.' },
    { icon: Shield, title: 'Google Authorization', desc: 'You sign in with Google and approve calendar access. Google redirects back with a one-time code.' },
    { icon: Key, title: 'Token Exchange', desc: 'The code is exchanged for access & refresh tokens, stored securely in the database.' },
    { icon: Video, title: 'Auto Meet Links', desc: 'Every booked session automatically gets a unique Google Meet link — zero manual work.' },
  ];

  return (
    <div className="min-h-full bg-white p-5 max-w-3xl mx-auto space-y-4">

      {/* Toast */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: -14, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -14, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed top-5 right-5 z-[9999] max-w-sm w-full"
          >
            <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 shadow-lg bg-white">
              {toast.type === 'error'
                ? <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                : <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />}
              <p className="text-xs text-gray-700 leading-snug flex-1">{toast.message}</p>
              <button onClick={() => setToast({ show: false, message: '', type: 'success' })} className="text-gray-300 hover:text-gray-500">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── HEADER ─── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-base font-bold text-gray-900">Google Calendar Integration</h1>
          <p className="text-xs text-gray-400 mt-0.5">Automate one-on-one session video links</p>
        </div>
        <button
          onClick={() => checkOAuth2Status(true)}
          disabled={refreshing || loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Syncing…' : 'Sync'}
        </button>
      </div>

      {/* ─── LOADING STATE ─── */}
      {loading && !oauthStatus && (
        <div className="border border-gray-100 rounded-xl p-8 flex items-center justify-center gap-2 text-xs text-gray-400">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Checking connection status…
        </div>
      )}

      {/* ─── CONNECTED STATE ─── */}
      {(isConnected || isExpired) && step === 4 && (
        <motion.div
          key="connected"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Status Hero */}
          <div className="border border-gray-100 rounded-xl overflow-hidden">
            {/* Status bar */}
            <div className={`px-5 py-3.5 flex items-center justify-between ${isConnected ? 'bg-emerald-50 border-b border-emerald-100' : 'bg-amber-50 border-b border-amber-100'}`}>
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2.5 w-2.5">
                  <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${isConnected ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                  <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isConnected ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                </span>
                <span className={`text-xs font-bold ${isConnected ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {isConnected ? 'Connected & Active' : 'Token Expired — Re-authentication Required'}
                </span>
              </div>
              {isConnected && (
                <div className="flex items-center gap-1.5">
                  <Lock className="w-3 h-3 text-emerald-600" />
                  <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider">Secure</span>
                </div>
              )}
            </div>

            {/* Body */}
            <div className="px-5 py-4">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-black flex items-center justify-center shrink-0">
                  <Video className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Integration is Active</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                    Meeting links will be automatically generated and attached to trainer slots when client sessions are booked.
                  </p>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-4">
                {[
                  { label: 'Link Status', value: isConnected ? 'Active & Secure' : 'Expired', icon: Shield, accent: isConnected ? 'text-emerald-600' : 'text-amber-600' },
                  { label: 'Stored Tokens', value: 'Access + Refresh', icon: Key, accent: 'text-blue-600' },
                  {
                    label: 'Configured On',
                    value: configuredDate,
                    icon: Calendar,
                    accent: 'text-indigo-600'
                  },
                  { label: 'Token Refreshing', value: 'Automatic', icon: RefreshCw, accent: 'text-gray-700' },
                ].map(({ label, value, icon: Icon, accent }) => (
                  <div key={label} className="border border-gray-100 rounded-lg p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">{label}</p>
                    <div className="flex items-center gap-1.5">
                      <Icon className={`w-3.5 h-3.5 shrink-0 ${accent}`} />
                      <p className={`text-xs font-bold ${accent}`}>{value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => checkOAuth2Status()}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-4 py-2 bg-black hover:bg-gray-800 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-all active:scale-95"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                  Test Connection
                </button>
                <button
                  onClick={() => { setStep(1); setOauthStatus(null); }}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Settings className="w-3.5 h-3.5" />
                  Reconnect Account
                </button>
              </div>
            </div>
          </div>

          {/* How does this work — collapsible */}
          <div className="border border-gray-100 rounded-xl overflow-hidden">
            <button
              onClick={() => setHowOpen(!howOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-md bg-black flex items-center justify-center shrink-0">
                  <Zap className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm font-bold text-gray-800">How does this integration work?</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${howOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {howOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-5 pt-1 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {howSteps.map((s, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50/60">
                        <div className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center shrink-0 mt-0.5">
                          <s.icon className="w-3.5 h-3.5 text-gray-700" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-gray-800">{s.title}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">{s.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}

      {/* ─── SETUP FLOW (steps 1–3) ─── */}
      {!isConnected && step !== 4 && (
        <motion.div
          key="setup"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Step Tracker */}
          <div className="border border-gray-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Setup Progress</p>
              <span className="text-xs font-semibold text-gray-400">Step {step} of 4</span>
            </div>
            <div className="relative flex justify-between">
              <div className="absolute top-4 left-4 right-4 h-px bg-gray-100 z-0" />
              <motion.div
                className="absolute top-4 left-4 h-px bg-blue-600 z-0"
                initial={{ width: '0%' }}
                animate={{ width: `${((step - 1) / 3) * 100}%` }}
                transition={{ duration: 0.35, ease: 'easeInOut' }}
              />
              {steps.map((s) => {
                const done = s.number < step;
                const active = s.number === step;
                return (
                  <div key={s.number} className="relative z-10 flex flex-col items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 text-xs font-bold transition-all duration-200 ${
                      done ? 'bg-blue-600 border-blue-600 text-white' :
                      active ? 'bg-white border-blue-600 text-blue-600' :
                      'bg-white border-gray-200 text-gray-400'
                    }`}>
                      {done ? <Check className="w-4 h-4" /> : s.number}
                    </div>
                    <span className={`text-[10px] font-semibold ${active ? 'text-blue-600' : done ? 'text-gray-700' : 'text-gray-400'}`}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step Panels */}
          <AnimatePresence mode="wait">

            {/* Step 1 */}
            {step === 1 && (
              <motion.div
                key="s1"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.18 }}
                className="border border-gray-100 rounded-xl overflow-hidden"
              >
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-[#4285F4]/10 flex items-center justify-center shrink-0">
                    <Calendar className="w-4 h-4 text-[#4285F4]" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900">Generate Authorization URL</h2>
                    <p className="text-xs text-gray-400">Connect your Google account to allow calendar & Meet access</p>
                  </div>
                </div>
                <div className="px-5 py-4 bg-gray-50/40 space-y-3">
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Clicking <strong className="text-gray-700 font-semibold">Sign in with Google</strong> will open Google's consent screen in a new tab. Log in with the Google account that will host the sessions.
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {['View and edit calendar events', 'Create Google Meet conferences'].map((perm) => (
                      <span key={perm} className="flex items-center gap-1.5 text-xs text-gray-500">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                        {perm}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="px-5 py-4 flex justify-end border-t border-gray-100">
                  <button
                    onClick={getGoogleAuthUrl}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 bg-[#4285F4] hover:bg-[#357ae8] disabled:bg-gray-100 disabled:text-gray-400 text-white rounded-lg text-xs font-semibold transition-all active:scale-95 shadow-sm"
                  >
                    {loading ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <svg className="w-3.5 h-3.5 bg-white rounded-sm p-0.5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                      </svg>
                    )}
                    {loading ? 'Generating…' : 'Sign in with Google'}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <motion.div
                key="s2"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.18 }}
                className="border border-gray-100 rounded-xl overflow-hidden"
              >
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                    <ExternalLink className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900">Complete Google Authorization</h2>
                    <p className="text-xs text-gray-400">A new tab has opened — approve the permissions in Google</p>
                  </div>
                </div>
                {authUrl && (
                  <div className="px-5 py-4 bg-gray-50/40">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">Authorization URL</p>
                    <div className="flex gap-2 items-center">
                      <div className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-400 font-mono truncate">
                        {authUrl}
                      </div>
                      <button
                        onClick={() => copyToClipboard(authUrl)}
                        className={`p-2 rounded-lg border transition-all shrink-0 ${copied ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}
                <div className="px-5 py-4 flex justify-between items-center border-t border-gray-100">
                  {authUrl ? (
                    <button
                      onClick={() => window.open(authUrl, '_blank')}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Re-open
                    </button>
                  ) : <span />}
                  <button
                    onClick={() => setStep(3)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg text-xs font-semibold transition-all active:scale-95"
                  >
                    I've Authorized →
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <motion.div
                key="s3"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.18 }}
                className="border border-gray-100 rounded-xl overflow-hidden"
              >
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                    <Key className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900">Enter Authorization Code</h2>
                    <p className="text-xs text-gray-400">Paste the code Google provided after you approved access</p>
                  </div>
                </div>
                <div className="px-5 py-4 bg-gray-50/40 space-y-3">
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      Never share this code. It is exchanged once for secure tokens and then discarded.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                      Authorization Code
                    </label>
                    <div className="relative">
                      <input
                        type={showCode ? 'text' : 'password'}
                        value={callbackCode}
                        onChange={(e) => setCallbackCode(e.target.value)}
                        placeholder="Paste your code here (e.g. 4/0AdQt8q…)"
                        className="w-full text-xs text-gray-700 bg-white px-3 py-2.5 pr-10 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCode(!showCode)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showCode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="px-5 py-4 flex items-center justify-between border-t border-gray-100">
                  <button
                    onClick={() => setStep(1)}
                    className="px-3 py-2 text-xs font-semibold text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleOAuth2Callback}
                    disabled={loading || !callbackCode.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-100 disabled:text-gray-400 text-white rounded-lg text-xs font-semibold transition-all active:scale-95"
                  >
                    {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />}
                    {loading ? 'Verifying…' : 'Complete Setup'}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* How it works — always visible during setup */}
          <div className="border border-gray-100 rounded-xl overflow-hidden">
            <button
              onClick={() => setHowOpen(!howOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-md bg-black flex items-center justify-center shrink-0">
                  <Zap className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm font-bold text-gray-800">How does this integration work?</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${howOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {howOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-5 pt-1 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {howSteps.map((s, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50/60">
                        <div className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center shrink-0 mt-0.5">
                          <s.icon className="w-3.5 h-3.5 text-gray-700" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-gray-800">{s.title}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">{s.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </div>
  );
}
